import { TrendingUp, TrendingDown, Minus, Clock } from "lucide-react";
import { useNovexData } from "@/hooks/useNovexData";
import { useLang } from "@/context/LanguageContext";
import { ExplainButton } from "@/components/ExplainButton";
import { SourceBadge } from "@/components/SourceBadge";

const GROUPS = [
  ["Inflation", "c.grpInflation"],
  ["Croissance & Emploi", "c.grpGrowth"],
  ["Taux & Marché", "c.grpRates"],
  ["Sentiment", "c.grpSentiment"],
];

function fmt(ind) {
  if (ind.value == null) return "—";
  const sign = ind.mode === "mom_diff" && ind.value > 0 ? "+" : "";
  return `${sign}${ind.value}${ind.unit || ""}`;
}

function Card({ ind, t }) {
  const diff = ind.value != null && ind.prev != null ? ind.value - ind.prev : null;
  const up = diff != null && diff > 0.001, down = diff != null && diff < -0.001;
  const Icon = up ? TrendingUp : down ? TrendingDown : Minus;
  const color = up ? "#F59E0B" : down ? "#38BDF8" : "#64748B";
  const timer = (() => {
    if (!ind.next_release) return null;
    const days = Math.ceil((new Date(ind.next_release + "T12:00:00Z").getTime() - Date.now()) / 86400000);
    if (days < 0) return { txt: t("c.timerUpcoming"), soon: false };
    if (days === 0) return { txt: t("c.timerToday"), soon: true };
    if (days === 1) return { txt: t("c.timerTomorrow"), soon: true };
    if (days <= 14) return { txt: t("c.timerInDays", { n: days }), soon: days <= 3 };
    return { txt: t("c.timerInWeeks", { n: Math.round(days / 7) }), soon: false };
  })();
  return (
    <div className="rounded-lg border border-[#222733] bg-[#0E1118] p-3" data-testid={`macro-${ind.label}`}>
      <div className="text-[11px] font-medium uppercase tracking-wide text-slate-500">{ind.label}</div>
      <div className="mt-1 flex items-baseline gap-2">
        <span className="font-mono text-2xl font-bold text-white">{fmt(ind)}</span>
        {diff != null && (
          <span className="flex items-center gap-0.5 text-xs font-mono" style={{ color }}>
            <Icon className="h-3 w-3" />{diff > 0 ? "+" : ""}{Math.round(diff * 100) / 100}
          </span>
        )}
      </div>
      <div className="mt-1 flex items-center justify-between gap-2">
        <span className="text-[10px] text-slate-600">{t("c.asOf", { date: ind.as_of })}</span>
        {timer && (
          <span
            className={`flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium ${timer.soon ? "bg-amber-500/10 text-amber-400" : "text-blue-300/80"}`}
            data-testid={`macro-timer-${ind.label}`}
            title={t("c.nextRelease")}
          >
            <Clock className="h-3 w-3" />{timer.txt}
          </span>
        )}
      </div>
    </div>
  );
}

export function MacroData({ compact = false }) {
  const { t } = useLang();
  const { status, data, error, source } = useNovexData("/macro/snapshot", { maxPolls: 20 });
  const inds = data?.indicators || [];

  if (status === "loading") {
    return <div className="grid grid-cols-2 gap-3 md:grid-cols-4">{[...Array(compact ? 4 : 8)].map((_, i) => <div key={i} className="h-20 animate-pulse rounded-lg bg-slate-800/50" />)}</div>;
  }
  if (error) return <p className="text-sm text-amber-500/80">{error}</p>;
  if (inds.length === 0) return <p className="text-sm text-slate-500">{t("c.macroNone")}</p>;

  const shown = compact ? inds.filter((i) => ["Inflation CPI (a/a)", "Core CPI (a/a)", "Taux de chômage", "Taux Fed (haut)", "Rendement 10 ans", "VIX (volatilité)"].includes(i.label)) : inds;

  if (compact) {
    return (
      <div>
        <div className="mb-2 flex items-center justify-between">
          <span className="text-[10px] text-slate-600">{t("c.fredSource")}</span><SourceBadge source="fallback" />
        </div>
        <div className="grid grid-cols-2 gap-2.5">{shown.map((ind) => <Card key={ind.label} ind={ind} t={t} />)}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <span className="text-xs text-slate-500">{t("c.fredFull")}</span>
        <ExplainButton kind="tableau de bord macroéconomique" text={`Indicateurs macro US: ${inds.map((i) => `${i.label}: ${fmt(i)}`).join("; ")}`} label={t("c.analyze")} testid="explain-macro" />
      </div>
      {GROUPS.map(([g, key]) => {
        const items = shown.filter((i) => i.group === g);
        if (!items.length) return null;
        return (
          <div key={g}>
            <h3 className="mb-2.5 font-heading text-sm font-semibold uppercase tracking-wider text-blue-300">{t(key)}</h3>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">{items.map((ind) => <Card key={ind.label} ind={ind} t={t} />)}</div>
          </div>
        );
      })}
    </div>
  );
}
